import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { IdNameModel } from '../models/IdNameModel';
import { GetPagedResponse } from '../models/getPagedResponse';
import { GetDetailsResponse } from '../models/getDetailsResponse';
import { CreatedBy } from '../models/userModel';
import { tap } from 'rxjs/internal/operators/tap';
import { PagedParams } from '../models/pagedParams';
import {
  orderByParam,
  pageNumberParam,
  pageSizeParam,
  searchTermParam,
} from '../../util/constant';

export interface MaintenanceRequestFilterValueModel {
  priorities: string[];
  statuses: string[];
  maintenanceCauses: IdNameModel[];
  maintenanceSubCauses: IdNameModel[];
  equipmentMachines: IdNameModel[];
  locations: IdNameModel[];
  maintenanceRequestPriorities: string[];
}

export interface FileModel {
  id: number;
  url: string;
}

export interface MaintenanceHistory {
  id: number;
  createdAt: string;

  oldStatus: string;
  newStatus: string;

  oldPriority: string;
  newPriority: string;

  oldAssignedTo: CreatedBy | null;
  newAssignedTo: CreatedBy | null;

  isLOTOFollowed: boolean;
  note: string;

  createdBy: CreatedBy;
}

export interface MaintenanceLog {
  id: number;
  createdAt: string;

  status: string;
  note: string;

  createdBy: CreatedBy;
  files: FileModel[];
}

export interface MaintenanceRequestModel {
  id: number;
  description: string;

  equipmentMachine: IdNameModel | null;
  location: IdNameModel;
  maintenanceCause: IdNameModel;
  maintenanceSubCause: IdNameModel | null;

  isOperational: boolean;
  isTaggedOut: boolean | null;
  isLOTOFollowed: boolean | null;

  assignedTo: CreatedBy | null;

  status: string;
  priority: string;

  createdAt: string; // ISO date string
  completedAt: string | null;

  createdBy: CreatedBy;
  updatedAt: string | null;
  updatedBy: CreatedBy | null;
}

export interface MaintenanceRequestDetails extends MaintenanceRequestModel {
  files: FileModel[];

  histories: MaintenanceHistory[];

  logs: MaintenanceLog[];
}

export interface UpdateMaintenanceRequestModel {
  assignedToId: number;
  status: string;
  priority: string;
  updateNote: string;
}

@Injectable({
  providedIn: 'root',
})
export class MaintenanceRequestService {
  baseUrl = environment.apiUrl + '/maintenance-requests';
  private http = inject(HttpClient);
  maintenanceRequestFilterValues =
    signal<MaintenanceRequestFilterValueModel | null>(null);

  getMaintenanceRequestFilterValues() {
    return this.http
      .get<
        GetDetailsResponse<MaintenanceRequestFilterValueModel>
      >(`${this.baseUrl}/get-filter-values`)
      .pipe(
        tap((response) =>
          this.maintenanceRequestFilterValues.set(response.data),
        ),
      );
  }

  getAuthorizedMaintenanceLogCreaters() {
    return this.http.get<GetDetailsResponse<CreatedBy[]>>(
      `${environment.apiUrl}/maintenance-logs/get-authorized-users`,
    );
  }

  getMaintenanceRequests(queryParams: PagedParams) {
    let params = new HttpParams();
    if (queryParams.pageNumber) {
      params = params.set(pageNumberParam, queryParams.pageNumber.toString());
    }
    if (queryParams.pageSize) {
      params = params.set(pageSizeParam, queryParams.pageSize.toString());
    }
    if (queryParams.orderBy) {
      params = params.set(orderByParam, queryParams.orderBy);
    }
    if (queryParams.searchTerm) {
      params = params.set(searchTermParam, queryParams.searchTerm);
    }

    if (queryParams.statuses) {
      params = params.set('statuses', queryParams.statuses);
    }

    if (queryParams.employeeId) {
      params = params.set('employeeId', queryParams.employeeId.toString());
    }

    if (queryParams.equipmentMachineId) {
      params = params.set(
        'equipmentMachineId',
        queryParams.equipmentMachineId.toString(),
      );
    }

    if (queryParams.locationId) {
      params = params.set('locationId', queryParams.locationId.toString());
    }

    if (queryParams.maintenanceCauseId) {
      params = params.set(
        'maintenanceCauseId',
        queryParams.maintenanceCauseId.toString(),
      );
    }

    if (queryParams.maintenanceSubCauseId) {
      params = params.set(
        'maintenanceSubCauseId',
        queryParams.maintenanceSubCauseId.toString(),
      );
    }

    if (queryParams.priority) {
      params = params.set('priority', queryParams.priority);
    }

    if (
      queryParams.isOperational !== undefined &&
      queryParams.isOperational !== null
    ) {
      params = params.set(
        'isOperational',
        queryParams.isOperational.toString(),
      );
    }

    if (
      queryParams.isTaggedOut !== undefined &&
      queryParams.isTaggedOut !== null
    ) {
      params = params.set('isTaggedOut', queryParams.isTaggedOut.toString());
    }

    if (
      queryParams.isLOTOFollowed !== undefined &&
      queryParams.isLOTOFollowed !== null
    ) {
      params = params.set(
        'isLOTOFollowed',
        queryParams.isLOTOFollowed.toString(),
      );
    }

    if (queryParams.startDate) {
      params = params.set('startDate', queryParams.startDate.toISOString());
    }

    if (queryParams.endDate) {
      params = params.set('endDate', queryParams.endDate.toISOString());
    }

    if (queryParams.assignedToId) {
      params = params.set('assignedToId', queryParams.assignedToId.toString());
    }

    return this.http.get<GetPagedResponse<MaintenanceRequestModel>>(
      `${this.baseUrl}`,
      {
        params,
      },
    );
  }
  getById(id: number) {
    return this.http.get<GetDetailsResponse<MaintenanceRequestDetails>>(
      `${this.baseUrl}/${id}`,
    );
  }

  createRequest(formData: FormData) {
    return this.http.post<GetDetailsResponse<number>>(this.baseUrl, formData);
  }

  updateRequest(id: number, payload: any) {
    return this.http.patch(`${this.baseUrl}/${id}`, payload);
  }
}
